import React, { useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import Snackbar from 'components/DesignSystem/Snackbar';
import RevisionTable from 'components/Flux/Revision/Table';
import RevisionSubTableModal from 'components/Flux/Revision/SubTableModal';
import FullscreenTable from 'components/Layout/MobileShell/FullscreenTable';
import FullControlPanelSheet from 'components/Flux/Automatic/Mobile/FullControlPanelSheet';
import QuerySummary from 'components/Flux/Automatic/Mobile/QuerySummary';
import SavePreferencesModal from 'components/Flux/Automatic/ControlPanel/SavedPreferences/PreferenceSavingModal';
import useRevisionData from 'utils/hooks/flux/automatic/useRevisionData';
import useRevisionScenarios from 'utils/hooks/flux/automatic/useRevisionScenarios';
import useFluxAutomaticZipDownload from 'utils/hooks/flux/automatic/useFluxAutomaticZipDownload';
import usePastSimulationNames from 'utils/hooks/flux/automatic/usePastSimulationNames';
import usePeriodControls from 'hooks/Flux/Automatic/Revision/usePeriodControls';
import { buildSubdivisionParam } from 'helper/flux/automatic';
import {
  selectFluxAutomaticControlPanelMltOption,
  selectFluxAutomaticControlPanelSelectedRevision,
  selectFluxAutomaticControlPanelSpatialSubdivision,
  selectFluxAutomaticControlPanelAutomaticStudyScenarios,
} from 'redux/reducers/fluxAutomaticControlPanelReducer';
import { cssVar } from 'constants/theme';

import './index.scss';

export default function RevisionMobile() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [modelName, setModelName] = useState('');
  const [period, setPeriod] = useState('');
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  const { pastSimulationNames } = usePastSimulationNames();
  const unidade = useSelector(selectFluxAutomaticControlPanelMltOption);
  const models = useSelector(selectFluxAutomaticControlPanelAutomaticStudyScenarios);
  const review = useSelector(selectFluxAutomaticControlPanelSelectedRevision);
  const subdivision = useSelector(selectFluxAutomaticControlPanelSpatialSubdivision);

  const { expectedDataResponse, revisionRows, isFetching } = useRevisionData();
  const {
    data, isFetching: isFetchingScenarios, errorMessage,
  } = useRevisionScenarios(modelName, period);

  const dataIsEmpty = !revisionRows.some((r) => r.values.some((v) => v.value !== '—'));
  const periodList = expectedDataResponse?.colLabels ?? [];
  const {
    toFirst, toPrevious, toNext, toLast, isFirstPeriod, isLastPeriod,
  } = usePeriodControls(period, periodList, setPeriod);

  const spatialSubdivisionParam = useMemo(() => buildSubdivisionParam(subdivision), [subdivision]);
  const { DownloadZipButton, DownloadConfirmationDialog } = useFluxAutomaticZipDownload({
    models,
    review,
    unidade,
    type: 'revision',
    pastSimulationNames,
    spatialSubdivisionParam,
  });

  const popDataModal = (chosenModel: string, chosenPeriod: string) => {
    setIsOpen(true);
    setModelName(chosenModel);
    setPeriod(chosenPeriod);
  };

  const extraChips = useMemo(() => {
    const result: string[] = [];
    if (review) result.push(`Revisão: ${review}`);
    return result;
  }, [review]);

  return (
    <div className="flux-automatic-revision-mobile">
      <DownloadConfirmationDialog />
      <SavePreferencesModal />
      <RevisionSubTableModal
        isOpen={isOpen}
        period={period}
        data={data?.data}
        revision={review}
        modelName={modelName}
        errorMessage={errorMessage}
        onClose={() => setIsOpen(false)}
        toFirst={toFirst}
        toPrevious={toPrevious}
        toNext={toNext}
        toLast={toLast}
        isFirstPeriod={isFirstPeriod}
        isLastPeriod={isLastPeriod}
        isFetchingScenarios={isFetchingScenarios}
        aboveFullscreen={fullscreenOpen}
      />

      <QuerySummary
        onOpenPanel={() => setSheetOpen(true)}
        extraChips={extraChips}
        action={models.length ? <DownloadZipButton /> : undefined}
      />

      {isFetching && <div className="text-center py-4"><Spinner /></div>}

      {!isFetching && dataIsEmpty && (
        <Snackbar>
          Nenhum dado para os filtros selecionados.
        </Snackbar>
      )}

      {!isFetching && !dataIsEmpty && (
        <div className="flux-table-card">
          <div className="flux-table-card-toolbar">
            <button
              type="button"
              aria-label="Tela cheia"
              className="flux-automatic-revision-mobile-action"
              onClick={() => setFullscreenOpen(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cssVar('text-strong')} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
              </svg>
            </button>
          </div>
          <div className="table-responsive flux-table-card-body flux-automatic-revision-mobile-table">
            <RevisionTable popDataModal={popDataModal} stickyRowLabel />
          </div>
        </div>
      )}

      <FullscreenTable open={fullscreenOpen} dismiss={() => setFullscreenOpen(false)}>
        <div className="table-responsive flux-automatic-revision-mobile-table">
          <RevisionTable popDataModal={popDataModal} stickyRowLabel />
        </div>
      </FullscreenTable>

      {/* tab="revision" omite a seção ACOMPH/MLT, igual ao ControlPanel do desktop. */}
      <FullControlPanelSheet
        open={sheetOpen}
        dismiss={() => setSheetOpen(false)}
        tab="revision"
      />
    </div>
  );
}
